import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronDown, Folder, FileText, Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from './axiosConfig';
import { translateAccountDescription } from './glDefaultAccounts';

const buildTree = (accounts) => {
  const byCode = {};
  accounts.forEach(a => { byCode[a.glaccount] = { ...a, children: [] }; });
  const roots = [];
  accounts.forEach(a => {
    const node = byCode[a.glaccount];
    if (a.parent_account && byCode[a.parent_account]) {
      byCode[a.parent_account].children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (nodes) => {
    nodes.sort((x, y) => (x.glaccount || '').localeCompare(y.glaccount || ''));
    nodes.forEach(n => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

// 🎯 بحث بيسيب بس الفروع اللي فيها تطابق، وبيفتحها أوتوماتيك (_forceOpen)
// عشان النتيجة تبان من غير ما اليوزر يفتح كل مستوى يدوي
const filterTree = (nodes, term) => {
  if (!term) return nodes;
  const lower = term.toLowerCase();
  const walk = (node) => {
    const selfMatch = node.glaccount.toLowerCase().includes(lower) || (node.description || '').toLowerCase().includes(lower);
    const filteredChildren = node.children.map(walk).filter(Boolean);
    if (selfMatch || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren, _forceOpen: filteredChildren.length > 0 };
    }
    return null;
  };
  return nodes.map(walk).filter(Boolean);
};

const TreeNode = ({ node, onSelect, level = 0, allowParentSelect, excludeCode, t }) => {
  const [isOpen, setIsOpen] = useState(!!node._forceOpen);
  useEffect(() => { if (node._forceOpen) setIsOpen(true); }, [node._forceOpen]);

  const hasChildren = node.children && node.children.length > 0;
  const isExcluded = excludeCode && node.glaccount === excludeCode;
  const canSelect = !isExcluded && (!hasChildren || allowParentSelect);

  return (
    <div className="select-none">
      <div
        className="flex items-center py-1.5 px-2 hover:bg-blue-50 cursor-pointer group rounded-sm transition-colors"
        style={level > 0 ? { marginInlineStart: 16, borderInlineStart: '1px solid #e5e7eb' } : undefined}
        onClick={() => hasChildren ? setIsOpen(!isOpen) : (canSelect && onSelect(node))}
      >
        <div className="flex items-center gap-2 flex-1">
          {hasChildren ? (
            isOpen ? <ChevronDown size={14} className="text-blue-600"/> : <ChevronRight size={14} className="text-gray-400"/>
          ) : (
            <div className="w-[14px]" />
          )}

          {hasChildren ? (
            <Folder size={16} className={`${isOpen ? 'text-yellow-500' : 'text-yellow-400'} fill-current`} />
          ) : (
            <FileText size={16} className="text-blue-400" />
          )}

          <span className={`text-[12px] ${!hasChildren ? 'font-bold text-blue-900' : 'text-gray-700'} ${isExcluded ? 'opacity-40 line-through' : ''}`}>
            {node.glaccount} <span className="text-gray-400 mx-1">|</span> {translateAccountDescription(node, t)}
          </span>
        </div>

        {canSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(node); }}
            className="opacity-0 group-hover:opacity-100 bg-[#1a3353] text-white text-[10px] px-3 py-0.5 rounded shadow-sm transition-all"
          >
            Select
          </button>
        )}
      </div>

      {isOpen && hasChildren && (
        <div className="animate-in slide-in-from-top-1 duration-200">
          {node.children.map(child => (
            <TreeNode key={child.glaccount} node={child} onSelect={onSelect} level={level + 1} allowParentSelect={allowParentSelect} excludeCode={excludeCode} t={t} />
          ))}
        </div>
      )}
    </div>
  );
};

// props:
// - allowParentSelect: لو true، حتى الحسابات اللي ليها فروع (parents) بتظهرلها
//   زرار Select - مستخدم في اختيار "الحساب الأب" حيث أي حساب موجود يصلح كأب
// - excludeCode: كود حساب يتشال من الاختيار (بيتشطب بس يفضل ظاهر في الشجرة) -
//   مستخدم عشان الحساب ميتحطش أب لنفسه
const GLAccountTree = ({ isOpen, onClose, onSelect, orgId, allowParentSelect = false, excludeCode = null, title = 'Chart of Accounts' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      api.get('/gl/accounts', { params: { org_id: orgId } })
        .then(res => { setTreeData(buildTree(res.data.data || [])); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setSearchTerm('');
    }
  }, [isOpen, orgId]);

  if (!isOpen) return null;

  const visibleTree = filterTree(treeData, searchTerm);

  return (
    <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`bg-white w-[550px] shadow-2xl border border-gray-300 flex flex-col max-h-[85vh] rounded-sm overflow-hidden animate-in zoom-in-95 duration-200 ${isRTL ? 'text-right' : 'text-left'}`}>

        <div className="bg-[#1a3353] text-white px-4 py-3 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors">
            <X size={18}/>
          </button>
        </div>

        <div className="p-3 bg-gray-50 border-b">
          <div className="relative">
            <input
              className={`w-full border border-gray-300 p-2 text-xs outline-none focus:border-blue-600 rounded ${isRTL ? 'pr-9' : 'pl-9'}`}
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className={`absolute top-2.5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-4 bg-white min-h-[400px] custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-blue-800 gap-2">
              <Loader2 className="animate-spin" size={32}/>
              <span className="text-xs font-bold">Loading Accounts...</span>
            </div>
          ) : visibleTree.length === 0 ? (
            <div className="text-center text-gray-400 text-xs py-10">No accounts found</div>
          ) : (
            <div className="space-y-1">
              {visibleTree.map(rootNode => (
                <TreeNode key={rootNode.glaccount} node={rootNode} onSelect={onSelect} allowParentSelect={allowParentSelect} excludeCode={excludeCode} t={t} />
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-50 p-3 border-t flex justify-end">
          <button onClick={onClose} className="bg-white border px-6 py-1.5 text-[11px] font-bold text-gray-600 hover:bg-gray-100 rounded shadow-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GLAccountTree;
